import {FILTER_KEYS, FILTER_VALUES} from '@github-ui/filter-core/constants/filter-constants'
import {SelectFilterProvider} from '@github-ui/filter-core/providers/select'
import {getMultiValueValidation} from '@github-ui/filter-core/providers/static'
import {
  type FilterKey,
  type FilterSuggestion,
  type FilterValueData,
  FilterValueType,
  type SuppliedFilterProviderOptions,
} from '@github-ui/filter-core/types'
import {getFilterValue, getUnescapedFilterValue, isFilterBlock} from '@github-ui/filter-core/utils'
import {categoryStatusVisual, STATUS_VISUALS} from '@github-ui/issue-pr-state/issue-status-visuals'
import type {Icon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import {issueQueryKind} from './issue-query-kind'

export type IssueStatusSearchOptions = {
  statuses: readonly string[]
  categories: readonly string[]
}

const CI_STATUS_CLASSES: Record<string, string> = {
  pending: 'fgColor-attention',
  success: 'fgColor-success',
  failure: 'fgColor-danger',
  queued: 'fgColor-muted',
  waiting: 'fgColor-muted',
  cancelled: 'fgColor-muted',
  skipped: 'fgColor-muted',
}

function coloredIcon(IconComponent: Icon, colorClass: string): Icon {
  return function StatusSuggestionIcon({className, ...props}) {
    return <IconComponent {...props} className={clsx(className, colorClass)} />
  }
}

function ciStatusSuggestions(): FilterSuggestion[] {
  return FILTER_VALUES.status.map(suggestion => {
    const colorClass = CI_STATUS_CLASSES[getFilterValue(suggestion.value) ?? '']
    return {
      ...suggestion,
      icon: suggestion.icon && colorClass ? coloredIcon(suggestion.icon, colorClass) : suggestion.icon,
    }
  })
}

function statusSuggestions(values: readonly string[]): FilterSuggestion[] {
  return values.map((value, index) => {
    const visual = STATUS_VISUALS[value]
    return {
      value,
      displayName: value,
      icon: visual && coloredIcon(visual.icon, `fgColor-${visual.color}`),
      type: FilterValueType.Value,
      priority: index + 1,
    }
  })
}

function categorySuggestions(categories: readonly string[]): FilterSuggestion[] {
  return categories.map((category, index) => {
    const value = category.replaceAll('_', '-')
    const visual = categoryStatusVisual(value)
    return {
      value,
      displayName: value.charAt(0).toUpperCase() + value.slice(1).replaceAll('-', ' '),
      aliases: value.includes('-') ? [value.replaceAll('-', '_')] : [],
      icon: visual && coloredIcon(visual.icon, `fgColor-${visual.color}`),
      type: FilterValueType.Value,
      priority: index + 1,
    }
  })
}

class CatalogFilterProvider extends SelectFilterProvider {
  readonly #catalogOrder = new Map<string, number>()

  constructor(
    key: FilterKey,
    catalog: FilterSuggestion[],
    additionalValues: FilterSuggestion[],
    options: SuppliedFilterProviderOptions,
  ) {
    super(key, [...additionalValues, ...catalog], options)
    for (const [index, suggestion] of catalog.entries()) {
      const value = getFilterValue(suggestion.value)
      if (typeof value !== 'string') throw new Error('Catalog suggestions require values')
      this.#catalogOrder.set(value, index)
    }
  }

  #catalogRank(suggestion: FilterSuggestion) {
    return this.#catalogOrder.get(getFilterValue(suggestion.value) ?? '')
  }

  protected isCatalogSuggestion(suggestion: FilterSuggestion) {
    return this.#catalogRank(suggestion) !== undefined
  }

  override getSuggestions(...args: Parameters<SelectFilterProvider['getSuggestions']>) {
    const matches = super.getSuggestions(...args)
    if (!matches) return matches

    // Preserve lifecycle order after filtering, without changing existing CI/action ranking.
    const offset = matches.reduce(
      (maximum, suggestion) =>
        this.#catalogRank(suggestion) === undefined ? Math.max(maximum, suggestion.priority) : maximum,
      0,
    )
    return matches.map(suggestion => {
      const rank = this.#catalogRank(suggestion)
      return rank === undefined ? suggestion : {...suggestion, priority: offset + rank + 1}
    })
  }
}

export class IssueStatusFilterProvider extends CatalogFilterProvider {
  constructor(statuses: readonly string[]) {
    super(
      {...FILTER_KEYS.status, description: 'Issue status or pull request commit status'},
      statusSuggestions(statuses),
      ciStatusSuggestions(),
      {filterTypes: {valueless: true, hasValue: true, multiValue: true, multiKey: true}},
    )
  }

  #isPullRequestValue(suggestion: FilterValueData) {
    const value = getUnescapedFilterValue(suggestion.value)
    return typeof value === 'string' && FILTER_VALUES.status.some(status => this.matchesFilterValue(status, value))
  }

  override validateFilterBlockValues(...args: Parameters<SelectFilterProvider['validateFilterBlockValues']>) {
    const values = super.validateFilterBlockValues(...args)
    if (values.length < 2 || values.every(value => this.#isPullRequestValue(value))) return values

    return values.map((value, index) => (index > 0 ? getMultiValueValidation(value, this.key) : value))
  }

  override getSuggestions(...args: Parameters<SelectFilterProvider['getSuggestions']>) {
    const matches = super.getSuggestions(...args)
    if (!matches) return matches
    const [query, block] = args
    const isList = isFilterBlock(block) && block.value.values.length > 1
    if (
      isList &&
      block.value.values.some(value => !('hasCaret' in value && value.hasCaret) && !this.#isPullRequestValue(value))
    ) {
      return []
    }

    const kind = issueQueryKind(query)
    const visible = matches.filter(suggestion => {
      if (isList && this.isCatalogSuggestion(suggestion)) return false
      if (kind === 'issue') return !this.#isPullRequestValue(suggestion)
      if (kind === 'pull-request') {
        return (
          !this.isCatalogSuggestion(suggestion) &&
          suggestion.type !== FilterValueType.NoValue &&
          suggestion.type !== FilterValueType.HasValue
        )
      }
      return true
    })
    const hasBothKinds =
      visible.some(suggestion => this.isCatalogSuggestion(suggestion)) &&
      visible.some(suggestion => this.#isPullRequestValue(suggestion))
    if (!hasBothKinds) return visible

    const ranked = visible.sort((left, right) => left.priority - right.priority)
    return [
      ...ranked.filter(suggestion => !this.isCatalogSuggestion(suggestion) && !this.#isPullRequestValue(suggestion)),
      ...ranked.filter(suggestion => this.isCatalogSuggestion(suggestion)),
      ...ranked.filter(suggestion => this.#isPullRequestValue(suggestion)),
    ].map((suggestion, priority) => ({...suggestion, priority}))
  }
}

export class IssueCategoryFilterProvider extends CatalogFilterProvider {
  constructor(categories: readonly string[]) {
    super(
      {
        key: 'category',
        displayName: 'Category',
        icon: FILTER_KEYS.status.icon,
        priority: FILTER_KEYS.status.priority,
      },
      categorySuggestions(categories),
      [],
      {filterTypes: {valueless: false, hasValue: false, multiValue: false, multiKey: true}},
    )
  }

  override getSuggestions(...args: Parameters<SelectFilterProvider['getSuggestions']>) {
    return issueQueryKind(args[0]) !== 'pull-request' ? super.getSuggestions(...args) : []
  }
}
